import React, {useState, useEffect} from 'react';
import {BaseCRUDPage} from '../common';
import {Input, Textarea, RequiredIndicator, Column, ShipperSelect, PortSelect} from '../../components';
import {consigneeService, shipperService, portService} from '../../services';
import {Consignee, ConsigneeCreate, ConsigneeUpdate, Shipper, Port} from '../../types';

interface ConsigneeFormData {
    code: string;
    name: string;
    port_id: string;
    address: string;
    street: string;
    city: string;
    transit: string;
    shipper_id: string;
}

const ConsigneesPage: React.FC = () => {
    const [shippers, setShippers] = useState<Shipper[]>([]);
    const [ports, setPorts] = useState<Port[]>([]);
    const [formData, setFormData] = useState<ConsigneeFormData>({
        code: '',
        name: '',
        port_id: '',
        address: '',
        street: '',
        city: '',
        transit: '',
        shipper_id: ''
    });

    // Load shippers and ports for dropdowns
    useEffect(() => {
        const loadData = async () => {
            try {
                const [shippersResult, portsResult] = await Promise.all([
                    shipperService.getAll({limit: 1000}),
                    portService.getAll({limit: 1000})
                ]);
                const sortedShippers = [...shippersResult].sort((a, b) => {
                    const codeA = parseInt(a.code) || 0;
                    const codeB = parseInt(b.code) || 0;
                    return codeA - codeB;
                });
                const sortedPorts = [...portsResult].sort((a, b) => a.code.localeCompare(b.code));
                setShippers(sortedShippers);
                setPorts(sortedPorts);
            } catch (error) {
                console.error('Error loading data:', error);
            }
        };
        loadData();
    }, []);

    // Define table columns
    const columns: Column[] = [
        {key: 'code', label: 'Consignee Code', sortable: true, searchable: true},
        {key: 'shipper_code', label: 'Shipper Code', sortable: true, searchable: true},
        {key: 'port_code', label: 'Port', sortable: true, searchable: true},
        {key: 'name', label: 'Name', sortable: true, searchable: true},
        {key: 'address', label: 'Address', sortable: true, searchable: true},
        {key: 'street', label: 'Street', sortable: true, searchable: true},
        {key: 'city', label: 'City', sortable: true, searchable: true},
        {key: 'transit', label: 'Transit', sortable: true, searchable: true}
    ];

    // Form fields component with error support
    const formFields = (fieldErrors: Record<string, string>) => (
        <div className="space-y-3">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Consignee Code <RequiredIndicator/>
                </label>
                <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({...prev, code: e.target.value}))}
                    required
                    placeholder="e.g., CON001"
                    error={!!fieldErrors.code}
                />
                {fieldErrors.code && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                  clipRule="evenodd"/>
                        </svg>
                        {fieldErrors.code}
                    </p>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Consignee Name <RequiredIndicator/>
                </label>
                <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    required
                    placeholder="e.g., ABC Logistics Ltd."
                    error={!!fieldErrors.name}
                />
                {fieldErrors.name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                  clipRule="evenodd"/>
                        </svg>
                        {fieldErrors.name}
                    </p>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Port
                </label>
                <PortSelect
                    ports={ports}
                    value={formData.port_id}
                    onChange={(portId) => setFormData(prev => ({...prev, port_id: portId}))}
                    placeholder="Select a port"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Shipper
                </label>
                <ShipperSelect
                    shippers={shippers}
                    value={formData.shipper_id}
                    onChange={(shipperId) => setFormData(prev => ({...prev, shipper_id: shipperId}))}
                    placeholder="Select a shipper"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Address
                </label>
                <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
                    placeholder="Enter company address..."
                    rows={2}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Street
                </label>
                <Input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData(prev => ({...prev, street: e.target.value}))}
                    placeholder="e.g., 123 Main Street"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                </label>
                <Input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({...prev, city: e.target.value}))}
                    placeholder="e.g., New York"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Transit
                </label>
                <Input
                    type="text"
                    value={formData.transit}
                    onChange={(e) => setFormData(prev => ({...prev, transit: e.target.value}))}
                    placeholder="e.g., Transit information"
                />
            </div>
        </div>
    );

    // Modal icon
    const modalIcon = (
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                 viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
        </div>
    );

    return (
        <BaseCRUDPage<Consignee, ConsigneeCreate, ConsigneeUpdate>
            title="Consignee Management"
            entityName="consignee"
            columns={columns}
            service={consigneeService}
            formFields={formFields}
            formData={formData}
            setFormData={setFormData}
            requiredFields={['code', 'name']}
            getInitialFormData={() => ({
                code: '',
                name: '',
                port_id: '',
                address: '',
                street: '',
                city: '',
                transit: '',
                shipper_id: ''
            })}
            toCreateData={(formData) => ({
                code: formData.code,
                name: formData.name,
                port_id: formData.port_id || undefined,
                address: formData.address || undefined,
                street: formData.street || undefined,
                city: formData.city || undefined,
                transit: formData.transit || undefined,
                shipper_id: formData.shipper_id || undefined,
                creator_id: '' // This will be set by the backend
            })}
            toUpdateData={(formData, original) => {
                const updateData: ConsigneeUpdate = {};
                if (formData.code !== original.code) updateData.code = formData.code;
                if (formData.name !== original.name) updateData.name = formData.name;
                if (formData.port_id !== (original.port_id || '')) updateData.port_id = formData.port_id || null;
                if (formData.address !== (original.address || '')) updateData.address = formData.address || null;
                if (formData.street !== (original.street || '')) updateData.street = formData.street || null;
                if (formData.city !== (original.city || '')) updateData.city = formData.city || null;
                if (formData.transit !== (original.transit || '')) updateData.transit = formData.transit || null;
                if (formData.shipper_id !== (original.shipper_id || '')) updateData.shipper_id = formData.shipper_id || null;
                return updateData;
            }}
            toFormData={(consignee) => ({
                code: consignee.code,
                name: consignee.name,
                port_id: consignee.port_id || '',
                address: consignee.address || '',
                street: consignee.street || '',
                city: consignee.city || '',
                transit: consignee.transit || '',
                shipper_id: consignee.shipper_id || ''
            })}
            modalIcon={modalIcon}
            modalTitle={(isEdit) => ({
                title: isEdit ? 'Edit Consignee' : 'Create New Consignee',
                subtitle: isEdit ? 'Update consignee information' : 'Add a new consignee to the system'
            })}
            customDeleteConfirm={(consignee) => `Are you sure you want to delete consignee "${consignee.name}"?`}
            defaultSearchColumn="code"
        />
    );
};

export default ConsigneesPage;
